import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { useStaffAccess } from "../../hooks/useIsStaffAdmin";
import { useHoursApprover } from "../../hooks/useHoursApprover";

// ---------------------------------------------------------------------------
// Qué pestañas de Nómina ve un colaborador (#1142).
//
// Quien NO administra al equipo (en CC-CONSOLE: todos menos Daniel e Isaías; en
// otros tenants lo decide el rol en el servidor, `GET /staffAccess`) solo ve
// «Mis pagos» y «Asistencia». Todo lo demás es de los dueños del Staff.
//
// Es una lista de PERMITIDAS a propósito: una pestaña nueva queda oculta para
// los colaboradores hasta que alguien decida agregarla aquí.
//
// Esto decide qué se PINTA y a dónde se navega; el control real de los datos
// sigue en el servidor (cada endpoint devuelve solo lo suyo o responde 403).
//
// #1346: «Horas por aprobar» entra a la lista. Ahí cada colaborador registra
// sus horas y sigue sus solicitudes; el servidor le manda solo las suyas
// (`GET /workHoursApprovals` → `meta.scope = "own"`) y nunca importes.
// ---------------------------------------------------------------------------

export const DEV_VISIBLE_TABS: readonly string[] = ["payments", "attendance", "hours-approval"];

/** Destino de un colaborador que teclea la URL de una pestaña oculta. */
export const DEV_FALLBACK_ROUTE = "/payroll/payments";

export function isTabVisibleToDev(tabId: string): boolean {
  return DEV_VISIBLE_TABS.includes(tabId);
}

/**
 * #1281: pestañas que además ve quien APRUEBA horas (tech lead o líder de squad,
 * según `GET /workHoursApprovalAccess`) aunque no administre Nómina. Desde
 * #1346 «Horas por aprobar» es una sección aparte que ven todos; lo que el
 * aprobador gana aquí es «Autorización de incidencias», con la tabla de horas
 * por colaborador (justificadas, por aprobar, pérdida o ganancia) de su gente.
 */
export const APPROVER_VISIBLE_TABS: readonly string[] = ["work-logs"];

export function isTabVisibleToApprover(tabId: string): boolean {
  return APPROVER_VISIBLE_TABS.includes(tabId);
}

/**
 * Envuelve cada ruta hija de /payroll. Si la pestaña no es para colaboradores,
 * espera la respuesta de `/staffAccess` (y, para «work-logs», la de quién
 * aprueba horas) antes de decidir: redirigir mientras carga sacaría a Isaías o
 * Daniel de su pantalla cada vez que recargan.
 */
export function PayrollTabGuard({ tabId, children }: { tabId: string; children: ReactNode }) {
  const { isStaffAdmin, loaded } = useStaffAccess(true, "payroll");
  const approver = useHoursApprover(isTabVisibleToApprover(tabId));
  if (isTabVisibleToDev(tabId) || isStaffAdmin) return <>{children}</>;
  if (isTabVisibleToApprover(tabId) && approver.canApprove) return <>{children}</>;
  if (!loaded || (isTabVisibleToApprover(tabId) && !approver.loaded)) return null;
  return <Navigate to={DEV_FALLBACK_ROUTE} replace />;
}
